/**
 * AdminMegaMenu — Top-bar de navigation premium pour la Caisse Pro.
 *
 * v2 (Feb 2026) — Refonte typographique & micro-interactions :
 *   - Police Space Grotesk pour les libellés (vibe SaaS premium type Linear/Stripe)
 *   - JetBrains Mono pour les nombres et keyboard shortcuts
 *   - Animated underline accent doré sur la famille active
 *   - Gradient halo radial dynamique en fond du dropdown (couleur de la famille)
 *   - Hover micro-scale + glow ring
 *   - Badges en gradient avec contour
 *   - Items du dropdown en grille avec barre accent gauche + chevron qui translate au hover
 */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Sunrise, LayoutGrid, Printer, Calculator, FileText, Building2,
  ShoppingCart, ClipboardList, ClipboardCheck, Truck, BookOpen,
  Activity, BarChart3, Receipt, Wallet, Coins, Package, Users,
  RefreshCw, MessageSquare, Settings, KeyRound, Sparkles, Globe,
  ChevronDown, ChevronRight, Search, Briefcase, Boxes, TrendingUp,
  BookMarked, ShieldCheck, ChefHat, X, Command, Menu, CalendarDays, Bell,
} from "lucide-react";

// ─────────────────────────── Configuration des familles ───────────────────────────
const MENU = [
  {
    key: "operations",
    label: "Opérations",
    short: "Opérations",
    icon: Briefcase,
    accent: "amber",
    description: "Tables, commandes, factures, locations",
    items: [
      { value: "tables", label: "Tables", sub: "Plan de salle", icon: LayoutGrid, color: "teal", roles: ["manager", "admin"] },
      { value: "commande", label: "Prise de commandes", sub: "Encaissement", icon: Calculator, color: "amber", roles: ["server", "manager", "admin"] },
      { value: "bons", label: "Bons", sub: "Tickets en cours", icon: Printer, color: "orange", badge: "bons" },
      { value: "invoices", label: "Factures", sub: "Historique facturation", icon: FileText, color: "blue" },
      { value: "proforma", label: "Proforma", sub: "Devis & offres", icon: FileText, color: "blue", roles: ["manager", "admin"] },
      { value: "locations", label: "Locations", sub: "Salles & équipements", icon: Building2, color: "purple", roles: ["manager", "admin"] },
      { value: "planning", label: "Planning hebdo", sub: "Équipes & horaires", icon: CalendarDays, color: "blue", roles: ["manager", "admin"], testid: "tab-planning" },
    ],
  },
  {
    key: "stock",
    label: "Stock & Appro",
    short: "Stock",
    icon: Boxes,
    accent: "sky",
    description: "Achats, fournisseurs, besoins, point de stock",
    items: [
      { value: "achats", label: "Achats", sub: "Dépenses & sorties caisse", icon: ShoppingCart, color: "purple", badge: "achats", roles: ["admin"] },
      { value: "courses", label: "Appro Manager", sub: "Demandes opérationnelles", icon: ShoppingCart, color: "amber", roles: ["admin"], testid: "tab-appro-manager" },
      { value: "needs", label: "Besoins", sub: "Listing du terrain", icon: ClipboardList, color: "indigo", badge: "needs", roles: ["manager", "admin"], testid: "tab-needs" },
      { value: "field_stock", label: "Point de stock", sub: "Inventaire en temps réel", icon: ClipboardCheck, color: "emerald", badge: "field_stock", roles: ["manager", "admin"], testid: "tab-field-stock" },
    ],
  },
  {
    key: "finances",
    label: "Finances & Rapports",
    short: "Finances",
    icon: TrendingUp,
    accent: "emerald",
    description: "Journée, point de caisse, statistiques, journal",
    items: [
      { value: "journee", label: "Journée", sub: "Ouverture / Clôture", icon: Sunrise, color: "amber", roles: ["admin", "manager"], testid: "tab-journee" },
      { value: "point_caisse", label: "Point de la Caisse", sub: "Fin de journée", icon: Receipt, color: "emerald", roles: ["admin", "manager"], testid: "tab-point-caisse" },
      { value: "stats", label: "Statistiques & Rapport", sub: "Performance globale", icon: BarChart3, color: "green", badge: "stats", roles: ["admin"] },
      { value: "forecasts", label: "Journal", sub: "Mouvements détaillés", icon: BookOpen, color: "purple", roles: ["admin"], testid: "tab-forecasts" },
      { value: "hebdo", label: "Faire le point", sub: "Synthèse hebdo", icon: BarChart3, color: "cyan", roles: ["manager", "admin"], testid: "caisse-tab-hebdo" },
      { value: "current-accounts", label: "Compte courant", sub: "Solde & relevés", icon: Wallet, color: "emerald", roles: ["admin"], testid: "tab-current-accounts" },
      { value: "tips", label: "Pourboires", sub: "Cagnotte & distribution", icon: Coins, color: "amber", badge: "tips", testid: "tab-tips" },
    ],
  },
  {
    key: "catalog",
    label: "Catalogue & Clients",
    short: "Catalogue",
    icon: BookMarked,
    accent: "pink",
    description: "Produits, clients, abonnements, notes",
    items: [
      { value: "products", label: "Produits", sub: "Référentiel", icon: Package, color: "purple" },
      { value: "clients", label: "Clients", sub: "Fidélité & CRM", icon: Users, color: "pink" },
      { value: "subscriptions", label: "Abonnements", sub: "Forfaits récurrents", icon: RefreshCw, color: "cyan", roles: ["manager", "admin"] },
      { value: "instructions", label: "Notes", sub: "Mémo équipe", icon: MessageSquare, color: "teal", badge: "notes", roles: ["manager", "admin"] },
    ],
  },
  {
    key: "notifications",
    label: "Notifications",
    short: "Notifs",
    icon: Bell,
    accent: "amber",
    description: "Toutes les notifications en attente",
    items: [
      { value: "notifications", label: "Toutes les notifications", sub: "Liste consolidée cliquable", icon: Bell, color: "amber", roles: ["manager", "admin"], testid: "tab-notifications" },
    ],
  },
  {
    key: "admin",
    label: "Administration",
    short: "Admin",
    icon: ShieldCheck,
    accent: "rose",
    description: "Utilisateurs, audit, codes invité, avis & notifs",
    items: [
      { value: "users", label: "Utilisateurs", sub: "Comptes & droits", icon: Settings, color: "red", roles: ["admin"] },
      { value: "guest-codes", label: "Codes Invité", sub: "Accès temporaires", icon: KeyRound, color: "amber", roles: ["admin"], testid: "tab-guest-codes" },
      { value: "audit", label: "Audit", sub: "Traçabilité complète", icon: ClipboardList, color: "rose", roles: ["admin"], testid: "tab-audit" },
      { value: "customer-reviews", label: "Avis clients", sub: "Feedback public", icon: MessageSquare, color: "pink", badge: "reviews", roles: ["admin"], testid: "tab-customer-reviews" },
      { value: "site-notifications", label: "Notifs Site", sub: "Bannières & alertes", icon: Globe, color: "emerald", roles: ["admin"], testid: "tab-site-notifications" },
    ],
  },
];

const SOLO_ITEMS = [
  { value: "mon_point", label: "Mon Point", sub: "Mes performances", icon: ClipboardList, color: "indigo", roles: ["server"] },
  { value: "points_agents", label: "Points Agents", sub: "Suivi équipe", icon: ClipboardList, color: "indigo", roles: ["manager"] },
];

// Couleurs Tailwind statiques (whitelist pour purge)
const COLOR_CLASSES = {
  amber: { bg: "bg-amber-500/15", border: "border-amber-500/40", text: "text-amber-300", glow: "shadow-amber-500/30", solid: "bg-amber-500", from: "from-amber-500/20", ring: "ring-amber-400/50", hex: "#f59e0b" },
  teal: { bg: "bg-teal-500/15", border: "border-teal-500/40", text: "text-teal-300", glow: "shadow-teal-500/30", solid: "bg-teal-500", from: "from-teal-500/20", ring: "ring-teal-400/50", hex: "#14b8a6" },
  orange: { bg: "bg-orange-500/15", border: "border-orange-500/40", text: "text-orange-300", glow: "shadow-orange-500/30", solid: "bg-orange-500", from: "from-orange-500/20", ring: "ring-orange-400/50", hex: "#f97316" },
  blue: { bg: "bg-blue-500/15", border: "border-blue-500/40", text: "text-blue-300", glow: "shadow-blue-500/30", solid: "bg-blue-500", from: "from-blue-500/20", ring: "ring-blue-400/50", hex: "#3b82f6" },
  purple: { bg: "bg-purple-500/15", border: "border-purple-500/40", text: "text-purple-300", glow: "shadow-purple-500/30", solid: "bg-purple-500", from: "from-purple-500/20", ring: "ring-purple-400/50", hex: "#a855f7" },
  sky: { bg: "bg-sky-500/15", border: "border-sky-500/40", text: "text-sky-300", glow: "shadow-sky-500/30", solid: "bg-sky-500", from: "from-sky-500/20", ring: "ring-sky-400/50", hex: "#0ea5e9" },
  emerald: { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-300", glow: "shadow-emerald-500/30", solid: "bg-emerald-500", from: "from-emerald-500/20", ring: "ring-emerald-400/50", hex: "#10b981" },
  indigo: { bg: "bg-indigo-500/15", border: "border-indigo-500/40", text: "text-indigo-300", glow: "shadow-indigo-500/30", solid: "bg-indigo-500", from: "from-indigo-500/20", ring: "ring-indigo-400/50", hex: "#6366f1" },
  cyan: { bg: "bg-cyan-500/15", border: "border-cyan-500/40", text: "text-cyan-300", glow: "shadow-cyan-500/30", solid: "bg-cyan-500", from: "from-cyan-500/20", ring: "ring-cyan-400/50", hex: "#06b6d4" },
  pink: { bg: "bg-pink-500/15", border: "border-pink-500/40", text: "text-pink-300", glow: "shadow-pink-500/30", solid: "bg-pink-500", from: "from-pink-500/20", ring: "ring-pink-400/50", hex: "#ec4899" },
  green: { bg: "bg-green-500/15", border: "border-green-500/40", text: "text-green-300", glow: "shadow-green-500/30", solid: "bg-green-500", from: "from-green-500/20", ring: "ring-green-400/50", hex: "#22c55e" },
  rose: { bg: "bg-rose-500/15", border: "border-rose-500/40", text: "text-rose-300", glow: "shadow-rose-500/30", solid: "bg-rose-500", from: "from-rose-500/20", ring: "ring-rose-400/50", hex: "#f43f5e" },
  red: { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-300", glow: "shadow-red-500/30", solid: "bg-red-500", from: "from-red-500/20", ring: "ring-red-400/50", hex: "#ef4444" },
};

const itemVisibleForRole = (item, role) => {
  if (item.hideRoles && item.hideRoles.includes(role)) return false;
  if (!item.roles || item.roles.length === 0) return true;
  return item.roles.includes(role);
};

const filteredFamily = (family, role) => ({
  ...family,
  items: family.items.filter((i) => itemVisibleForRole(i, role)),
});

export default forwardRef(function AdminMegaMenu({
  activeTab,
  setActiveTab,
  currentUser,
  badges = {},
  unreadNotesCount = 0,
  fieldStockPendingCount = 0,
  cuisineNeedsPending = { pending: 0, urgent: 0 },
}, ref) {
  const role = currentUser?.role || "server";
  const [openFamily, setOpenFamily] = useState(null);
  const [search, setSearch] = useState("");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const containerRef = useRef(null);

  const resolveBadge = () => 0;

  const visibleMenu = useMemo(() => MENU.map((f) => filteredFamily(f, role)).filter((f) => f.items.length > 0), [role]);
  const visibleSolo = useMemo(() => SOLO_ITEMS.filter((i) => itemVisibleForRole(i, role)), [role]);

  const activeFamily = useMemo(() => {
    for (const f of MENU) {
      if (f.items.some((i) => i.value === activeTab)) return f;
    }
    return null;
  }, [activeTab]);

  const activeItem = useMemo(() => {
    if (!activeFamily) return null;
    return activeFamily.items.find((i) => i.value === activeTab) || null;
  }, [activeFamily, activeTab]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenFamily(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setPaletteOpen(false);
        setOpenFamily(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const allFlatItems = useMemo(() => {
    const flat = [];
    visibleMenu.forEach((f) => f.items.forEach((i) => flat.push({ ...i, familyKey: f.key, familyLabel: f.label, familyAccent: f.accent })));
    visibleSolo.forEach((i) => flat.push({ ...i, familyKey: "solo", familyLabel: "Mon espace", familyAccent: "indigo" }));
    return flat;
  }, [visibleMenu, visibleSolo]);

  const paletteResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allFlatItems.slice(0, 20);
    return allFlatItems.filter((i) =>
      i.label.toLowerCase().includes(q) || i.familyLabel.toLowerCase().includes(q) || (i.sub || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [search, allFlatItems]);

  const handleSelect = (value) => {
    setActiveTab(value);
    setOpenFamily(null);
    setPaletteOpen(false);
    setSearch("");
  };

  const familyBadgeTotal = (family) => family.items.reduce((acc, it) => acc + (it.badge ? resolveBadge(it.badge) : 0), 0);

  // Détection mobile (< 768px = md breakpoint). Window-based, géré côté client.
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null); // Famille dépliée en mode mobile
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  // Pré-déplier la famille active à l'ouverture du sheet mobile
  useEffect(() => {
    if (mobileSheetOpen && activeFamily) setMobileExpanded(activeFamily.key);
  }, [mobileSheetOpen, activeFamily]);

  const handleSelectMobile = (value) => {
    setActiveTab(value);
    setMobileSheetOpen(false);
    setMobileExpanded(null);
  };

  // Expose openSheet() au parent (utilisé par AdminBottomNav)
  useImperativeHandle(ref, () => ({
    openSheet: () => setMobileSheetOpen(true),
    openPalette: () => setPaletteOpen(true),
  }), []);

  return (
    <>
    <div className="md:hidden mb-4 mm-root" data-testid="admin-mega-menu" ref={containerRef}>
      <style>{`
        .mm-root, .mm-root * { font-family: 'Space Grotesk', 'Outfit', system-ui, -apple-system, sans-serif; }
        .mm-mono { font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace !important; font-variant-numeric: tabular-nums; }

        @keyframes mm-fade-in {
          from { opacity: 0; transform: translateY(-6px) scale(0.985); filter: blur(2px); }
          to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        .mm-dropdown { animation: mm-fade-in 220ms cubic-bezier(0.16, 1, 0.3, 1); transform-origin: top center; }

        @keyframes mm-underline-grow {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        .mm-underline { animation: mm-underline-grow 280ms cubic-bezier(0.16, 1, 0.3, 1); transform-origin: left center; }

        .mm-grain {
          background-image:
            radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            radial-gradient(rgba(255,255,255,0.015) 1px, transparent 1px);
          background-size: 4px 4px, 8px 8px;
          background-position: 0 0, 2px 2px;
        }

        .mm-bar-glow {
          box-shadow:
            0 1px 0 rgba(255,255,255,0.04) inset,
            0 -1px 0 rgba(0,0,0,0.3) inset,
            0 20px 40px -20px rgba(0,0,0,0.6);
        }

        .mm-family-btn { letter-spacing: 0.02em; }
        .mm-family-btn[data-active="true"] { letter-spacing: 0.04em; }

        .mm-family-btn .mm-active-bar {
          position: absolute; left: 12px; right: 12px; bottom: 4px;
          height: 2px; border-radius: 2px;
          background: linear-gradient(90deg, transparent, currentColor, transparent);
        }

        .mm-item-card { transition: transform 180ms cubic-bezier(0.16,1,0.3,1), border-color 180ms, background 180ms; }
        .mm-item-card:hover { transform: translateY(-2px); }
        .mm-item-card .mm-chevron { transition: transform 180ms; opacity: 0.4; }
        .mm-item-card:hover .mm-chevron { transform: translateX(3px); opacity: 1; }

        .mm-badge {
          background: linear-gradient(180deg, #f43f5e 0%, #be123c 100%);
          box-shadow: 0 1px 0 rgba(255,255,255,0.25) inset, 0 2px 6px rgba(244,63,94,0.45);
          font-family: 'JetBrains Mono', monospace;
          font-feature-settings: 'tnum';
          letter-spacing: -0.02em;
        }

        .mm-kbd {
          font-family: 'JetBrains Mono', monospace;
          background: linear-gradient(180deg, rgba(51,65,85,0.8) 0%, rgba(30,41,59,0.9) 100%);
          box-shadow: 0 1px 0 rgba(255,255,255,0.07) inset, 0 1px 2px rgba(0,0,0,0.4);
        }

        @keyframes mm-mobile-fold {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 1200px; }
        }
        .mm-mobile-section { animation: mm-mobile-fold 280ms cubic-bezier(0.16, 1, 0.3, 1); overflow: hidden; }
      `}</style>

      {/* ═══════════════════════ MOBILE (< md) — désactivé (remplacé par MobileMenuHub) ═══════════════════════ */}
      {false && isMobile ? (
        <>
          <div className="relative bg-gradient-to-b from-slate-900/95 via-slate-900/90 to-slate-950/95 border border-slate-800/80 rounded-xl mm-bar-glow backdrop-blur-md overflow-hidden">
            <div className="absolute inset-0 mm-grain opacity-60 pointer-events-none" />
            {activeFamily && (
              <div
                className="absolute inset-0 pointer-events-none opacity-30"
                style={{ background: `radial-gradient(ellipse at center, ${COLOR_CLASSES[activeFamily.accent]?.hex || "#f59e0b"}33 0%, transparent 70%)` }}
              />
            )}
            <div className="relative flex items-center gap-2 p-2.5">
              {/* Bouton menu hamburger qui ouvre la Sheet */}
              <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2.5 flex-1 min-w-0 px-3 py-2.5 rounded-lg bg-slate-800/60 hover:bg-slate-800/90 border border-slate-700/70 transition text-left"
                    data-testid="menu-mobile-trigger"
                  >
                    {activeItem && activeFamily ? (
                      <>
                        <div className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${COLOR_CLASSES[activeItem.color]?.bg} border ${COLOR_CLASSES[activeItem.color]?.border}`}>
                          <activeItem.icon className={`w-4 h-4 ${COLOR_CLASSES[activeItem.color]?.text}`} strokeWidth={2.4} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-slate-500 text-[9px] uppercase tracking-[0.15em] font-bold leading-none mb-0.5">{activeFamily.label}</p>
                          <p className="text-white text-[13px] font-bold tracking-tight truncate leading-tight">{activeItem.label}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center bg-amber-500/20 border border-amber-500/40">
                          <Menu className="w-4 h-4 text-amber-300" strokeWidth={2.4} />
                        </div>
                        <p className="text-white text-[13px] font-bold tracking-tight">Menu</p>
                      </>
                    )}
                    <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" strokeWidth={2.4} />
                  </button>
                </SheetTrigger>

                <SheetContent
                  side="left"
                  className="w-[88vw] max-w-md bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900 border-r border-slate-800 p-0 overflow-hidden flex flex-col mm-root"
                >
                  <div className="absolute inset-0 mm-grain opacity-50 pointer-events-none" />

                  {/* Header de la Sheet */}
                  <div className="relative px-4 pt-5 pb-3 border-b border-slate-800/80 flex-shrink-0">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                        <span className="text-slate-900 font-black text-sm">M</span>
                      </div>
                      <div>
                        <p className="text-white font-black text-[15px] tracking-tight leading-tight">Caisse Pro</p>
                        <p className="text-slate-500 text-[10px] uppercase tracking-[0.15em] font-bold leading-tight">Navigation</p>
                      </div>
                    </div>
                    {/* Bouton recherche dans la Sheet */}
                    <button
                      type="button"
                      onClick={() => { setMobileSheetOpen(false); setPaletteOpen(true); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-slate-800/70 hover:bg-slate-800 border border-slate-700/70 transition text-slate-400 hover:text-white text-[13px]"
                      data-testid="menu-mobile-search"
                    >
                      <Search className="w-4 h-4" strokeWidth={2.4} />
                      <span>Rechercher un module…</span>
                    </button>
                  </div>

                  {/* Liste des familles en accordéon */}
                  <div className="relative flex-1 overflow-y-auto px-2 py-2">
                    {visibleMenu.map((family) => {
                      const Icon = family.icon;
                      const c = COLOR_CLASSES[family.accent] || COLOR_CLASSES.amber;
                      const isExpanded = mobileExpanded === family.key;
                      const isFamilyActive = activeFamily?.key === family.key;
                      const bCount = familyBadgeTotal(family);
                      return (
                        <div key={family.key} className="mb-1">
                          <button
                            type="button"
                            onClick={() => setMobileExpanded(isExpanded ? null : family.key)}
                            className={`w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg transition ${
                              isFamilyActive ? `${c.bg} ring-1 ring-inset ${c.border}` : "hover:bg-slate-800/70"
                            }`}
                            data-testid={`menu-mobile-family-${family.key}`}
                          >
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.bg} border ${c.border}`}>
                              <Icon className={`w-4 h-4 ${c.text}`} strokeWidth={2.4} />
                            </div>
                            <div className="min-w-0 flex-1 text-left">
                              <p className={`text-[13.5px] font-bold tracking-tight ${isFamilyActive ? c.text : "text-white"}`}>
                                {family.label}
                              </p>
                              <p className="text-slate-500 text-[10.5px] font-medium leading-tight truncate">{family.description}</p>
                            </div>
                            {bCount > 0 && (
                              <span className="mm-badge min-w-[18px] h-[18px] px-1.5 rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                                {bCount > 99 ? "99+" : bCount}
                              </span>
                            )}
                            <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? "rotate-180" : ""}`} strokeWidth={2.4} />
                          </button>

                          {isExpanded && (
                            <div className="mm-mobile-section pl-2 pt-1 pb-1 space-y-1">
                              {family.items.map((item) => {
                                const ItemIcon = item.icon;
                                const isActive = activeTab === item.value;
                                const itemColor = COLOR_CLASSES[item.color] || COLOR_CLASSES.amber;
                                const bVal = item.badge ? resolveBadge(item.badge) : 0;
                                return (
                                  <button
                                    key={item.value}
                                    type="button"
                                    onClick={() => handleSelectMobile(item.value)}
                                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition text-left ${
                                      isActive
                                        ? `bg-gradient-to-r ${itemColor.from} to-transparent ring-1 ring-inset ${itemColor.border}`
                                        : "hover:bg-slate-800/60 border border-transparent"
                                    }`}
                                    data-testid={item.testid || `menu-mobile-item-${item.value}`}
                                  >
                                    {isActive && <span className={`w-[3px] h-7 rounded-r flex-shrink-0 ${itemColor.solid}`} />}
                                    <div className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${itemColor.bg} border ${itemColor.border}`}>
                                      <ItemIcon className={`w-3.5 h-3.5 ${itemColor.text}`} strokeWidth={2.2} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className={`text-[12.5px] font-bold tracking-tight leading-tight ${isActive ? itemColor.text : "text-slate-100"}`}>
                                        {item.label}
                                      </p>
                                      {item.sub && (
                                        <p className="text-slate-500 text-[10px] font-medium leading-tight truncate mt-0.5">{item.sub}</p>
                                      )}
                                    </div>
                                    {bVal > 0 && (
                                      <span className="mm-badge min-w-[18px] h-[18px] px-1.5 rounded-full text-white text-[10px] font-bold flex items-center justify-center">
                                        {bVal > 99 ? "99+" : bVal}
                                      </span>
                                    )}
                                    <ChevronRight className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.4} />
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Items solo (server/manager) */}
                    {visibleSolo.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-800/60 space-y-1">
                        {visibleSolo.map((item) => {
                          const Icon = item.icon;
                          const isActive = activeTab === item.value;
                          const c = COLOR_CLASSES[item.color] || COLOR_CLASSES.amber;
                          return (
                            <button
                              key={item.value}
                              type="button"
                              onClick={() => handleSelectMobile(item.value)}
                              className={`w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg transition text-left ${
                                isActive ? `${c.bg} ring-1 ring-inset ${c.border}` : "hover:bg-slate-800/70"
                              }`}
                              data-testid={`menu-mobile-solo-${item.value}`}
                            >
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.bg} border ${c.border}`}>
                                <Icon className={`w-4 h-4 ${c.text}`} strokeWidth={2.4} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-[13.5px] font-bold tracking-tight ${isActive ? c.text : "text-white"}`}>{item.label}</p>
                                {item.sub && <p className="text-slate-500 text-[10.5px] leading-tight">{item.sub}</p>}
                              </div>
                              <ChevronRight className="w-4 h-4 text-slate-500" strokeWidth={2.4} />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Footer Sheet */}
                  <div className="relative px-4 py-3 border-t border-slate-800/80 flex-shrink-0">
                    <p className="text-slate-600 text-[10px] uppercase tracking-[0.15em] font-bold text-center">
                      {currentUser?.full_name || currentUser?.username || "—"} · {role}
                    </p>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </>
      ) : (
        <></>
      )}

      {/* Breadcrumb du module actuel */}
      {activeFamily && activeItem && (
        <div className="mt-2.5 flex items-center gap-1.5 text-[11.5px] px-1.5" data-testid="menu-breadcrumb">
          <div className={`w-[18px] h-[18px] rounded flex items-center justify-center ${COLOR_CLASSES[activeFamily.accent]?.bg} border ${COLOR_CLASSES[activeFamily.accent]?.border}`}>
            <activeFamily.icon className={`w-2.5 h-2.5 ${COLOR_CLASSES[activeFamily.accent]?.text}`} strokeWidth={2.6} />
          </div>
          <span className="text-slate-500 uppercase tracking-[0.1em] font-semibold text-[10.5px]">{activeFamily.label}</span>
          <ChevronRight className="w-3 h-3 text-slate-600" strokeWidth={2.4} />
          <span className={`font-bold tracking-tight ${COLOR_CLASSES[activeItem.color]?.text || "text-amber-300"}`}>{activeItem.label}</span>
          {activeItem.sub && (
            <span className="text-slate-500 text-[10.5px] hidden sm:inline ml-1">— {activeItem.sub}</span>
          )}
        </div>
      )}

    </div>

      {/* ──────── Command Palette (Ctrl+K) ──────── */}
      {paletteOpen && (
        <div
          className="fixed inset-0 z-[9000] bg-slate-950/85 backdrop-blur-md flex items-start justify-center pt-[12vh] px-4"
          onClick={() => setPaletteOpen(false)}
          data-testid="menu-palette"
        >
          <div
            className="w-full max-w-xl bg-gradient-to-b from-slate-900 to-slate-950 border border-amber-500/30 rounded-2xl shadow-2xl shadow-amber-500/15 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-800/80">
              <Search className="w-4 h-4 text-amber-400" strokeWidth={2.4} />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un module… (Stats, Achats, Audit, Tables…)"
                className="flex-1 bg-transparent border-0 outline-none text-white text-[14px] font-medium placeholder:text-slate-500"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                data-testid="menu-palette-input"
              />
              <kbd className="mm-kbd text-[10px] text-slate-400 px-2 py-1 rounded">esc</kbd>
            </div>
            <div className="max-h-[55vh] overflow-y-auto p-2">
              {paletteResults.length === 0 ? (
                <p className="text-slate-500 text-xs text-center py-8 italic">Aucun résultat pour « {search} »</p>
              ) : (
                paletteResults.map((item) => {
                  const Icon = item.icon;
                  const c = COLOR_CLASSES[item.color] || COLOR_CLASSES.amber;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => handleSelect(item.value)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-800/70 transition text-left group"
                      data-testid={`menu-palette-item-${item.value}`}
                    >
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${c.bg} border ${c.border}`}>
                        <Icon className={`w-4 h-4 ${c.text}`} strokeWidth={2.2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-[13px] font-semibold truncate tracking-tight">{item.label}</p>
                        <p className="text-slate-500 text-[10.5px] truncate font-medium">
                          {item.familyLabel}{item.sub ? ` · ${item.sub}` : ""}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all" strokeWidth={2.4} />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
});
